import languageManager from '../language/index.js';
import { getLanguagePreference } from '../utils/localStorageManager.js';

/**
 * 語言選擇器元件
 * 用於在頁面上顯示和切換語言的 UI 元件
 */
class LanguageSelector {
    /**
     * 建立語言選擇器實例
     * @param {string} containerId - 容器的 ID（預設：'language-selector'）
     */
    constructor(containerId = 'language-selector') {
        this.containerId = containerId;
        this.container = null;
        this.languageSelect = null;
        this.initialized = false;
    }

    /**
     * 初始化語言選擇器
     * 設置必要的 DOM 元素和事件監聽器
     */
    init() {
        if (this.initialized) return;

        // 首先初始化語言系統
        languageManager.init();

        // 尋找或建立容器
        this.container = document.getElementById(this.containerId);
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.id = this.containerId;
            this.container.className = 'language-selector-container';
        }

        this.render();
        this.attachEventListeners();

        // 監聽語言變更事件
        window.addEventListener('languageChanged', this.updateDisplay.bind(this));

        this.initialized = true;
    }

    /**
     * 渲染語言選擇器的 HTML
     * 包含下拉選單和當前語言顯示
     */
    render() {
        const currentLanguage = getLanguagePreference() || 'zh-Hant';
        const availableLanguages = languageManager.getAvailable();
        const t = languageManager.t;
        const currentLangName = this.getCurrentLanguageName(currentLanguage);

        this.container.innerHTML = `
            <div class="dropdown">
                <button class="btn btn-outline-secondary dropdown-toggle" type="button" id="language-dropdown" 
                    data-bs-toggle="dropdown" aria-expanded="false">
                    <i class="bi bi-globe"></i>
                    <span class="language-selector-label">${t('language.select')}</span>
                </button>
                <ul class="dropdown-menu" aria-labelledby="language-dropdown">
                    ${availableLanguages.map(lang => `
                        <li>
                            <a class="dropdown-item ${lang.code === currentLanguage ? 'active' : ''}" 
                               href="#" data-language="${lang.code}">
                                ${lang.name}
                            </a>
                        </li>
                    `).join('')}
                </ul>
            </div>
            <div class="current-language-display">
                <small class="text-muted">${t('language.current')}: ${currentLangName}</small>
            </div>
        `;
    }

    /**
     * 根據語言代碼獲取語言名稱
     * @param {string} langCode - 語言代碼
     * @returns {string} 語言名稱
     */
    getCurrentLanguageName(langCode) {
        const languages = languageManager.getAvailable();
        const lang = languages.find(l => l.code === langCode);
        return lang ? lang.name : langCode;
    }

    /**
     * 附加事件監聽器
     * 處理語言選擇的點擊事件
     */
    attachEventListeners() {
        const languageItems = this.container.querySelectorAll('[data-language]');
        languageItems.forEach(item => {
            item.addEventListener('click', (e) => {
                e.preventDefault();
                const language = e.currentTarget.getAttribute('data-language');
                languageManager.switch(language);
            });
        });
    }

    /**
     * 更新顯示
     * 當語言變更時更新 UI 元素
     */
    updateDisplay() {
        const currentLanguage = getLanguagePreference();
        const items = this.container.querySelectorAll('[data-language]');
        const t = languageManager.t;
        const currentLangName = this.getCurrentLanguageName(currentLanguage);

        const label = this.container.querySelector('.language-selector-label');
        if (label) label.textContent = t('language.select');

        // 更新當前語言顯示
        const langDisplay = this.container.querySelector('.current-language-display small');
        if (langDisplay) {
            langDisplay.textContent = `${t('language.current')}: ${currentLangName}`;
        }

        // 更新下拉選單項目的活動狀態
        items.forEach(item => {
            const itemLang = item.getAttribute('data-language');
            if (itemLang === currentLanguage) {
                item.classList.add('active');
            } else {
                item.classList.remove('active');
            }
        });
    }
}

export default LanguageSelector;
